import type { NFTData, NFTFetchResult } from '../../types';
import { getAsset, searchAssetsByCollection, fetchMetadataFromUri } from './heliusApi';
import { parseHeliusAsset } from './metaplexParser';
import { isValidSolanaAddress } from '../../utils';
import { NFT_CONTRACT_ADDRESS } from '../../constants';

async function enrichWithUriMetadata(nftData: NFTData): Promise<NFTData> {
  // If no jsonUri, can't fetch
  if (!nftData.jsonUri) {
    return nftData;
  }

  console.log('Fetching metadata from URI:', nftData.jsonUri);
  const metadata = await fetchMetadataFromUri(nftData.jsonUri);

  if (!metadata) {
    return nftData;
  }

  // Extract attributes from URI metadata if not already present
  const attributes = nftData.attributes.length > 0
    ? nftData.attributes
    : (metadata.attributes?.map((attr) => ({
        traitType: attr.trait_type || 'Unknown',
        value: attr.value ?? '',
      })) ?? []);

  // Extract layer assets from properties.files
  const layerAssets = metadata.properties?.files
    ?.filter((file) => file.id && file.uri)
    .map((file) => ({
      id: file.id!,
      uri: file.uri,
      type: file.type || 'image/png',
    })) ?? [];

  console.log('Extracted layer assets:', layerAssets);

  return {
    ...nftData,
    attributes,
    description: nftData.description || metadata.description || '',
    layerAssets,
  };
}

const nftCache = new Map<string, NFTData>();

export async function fetchNFT(mintAddress: string): Promise<NFTFetchResult> {
  const trimmedAddress = mintAddress.trim();

  if (!isValidSolanaAddress(trimmedAddress)) {
    return {
      success: false,
      data: null,
      error: 'Invalid Solana address format',
    };
  }

  const cached = nftCache.get(trimmedAddress);
  if (cached) {
    return {
      success: true,
      data: cached,
      error: null,
    };
  }

  try {
    const asset = await getAsset(trimmedAddress);
    console.log('Raw Helius response:', JSON.stringify(asset, null, 2));
    let nftData = parseHeliusAsset(asset);

    if (!nftData.collection || nftData.collection.address !== NFT_CONTRACT_ADDRESS) {
      return {
        success: false,
        data: null,
        error: 'This NFT is not from the supported collection',
      };
    }

    if (!nftData.imageUrl) {
      return {
        success: false,
        data: null,
        error: 'NFT has no image',
      };
    }

    // Fetch traits from URI if not available from Helius
    nftData = await enrichWithUriMetadata(nftData);

    nftCache.set(trimmedAddress, nftData);

    return {
      success: true,
      data: nftData,
      error: null,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch NFT';

    if (message.includes('not found') || message.includes('No asset')) {
      return {
        success: false,
        data: null,
        error: 'NFT not found. Please check the mint address.',
      };
    }

    return {
      success: false,
      data: null,
      error: message,
    };
  }
}

export function getCachedNFT(mintAddress: string): NFTData | null {
  return nftCache.get(mintAddress) ?? null;
}

export function clearNFTCache(): void {
  nftCache.clear();
}

export async function fetchNFTByNumber(nftNumber: number): Promise<NFTFetchResult> {
  if (nftNumber <= 0 || !Number.isInteger(nftNumber)) {
    return {
      success: false,
      data: null,
      error: 'Please enter a valid NFT number',
    };
  }

  try {
    const asset = await searchAssetsByCollection(NFT_CONTRACT_ADDRESS, nftNumber);
    console.log('Raw Helius response (by number):', JSON.stringify(asset, null, 2));

    if (!asset) {
      return {
        success: false,
        data: null,
        error: `NFT #${nftNumber} not found in the collection`,
      };
    }

    let nftData = parseHeliusAsset(asset);

    if (!nftData.imageUrl) {
      return {
        success: false,
        data: null,
        error: 'NFT has no image',
      };
    }

    // Fetch traits from URI if not available from Helius
    nftData = await enrichWithUriMetadata(nftData);

    nftCache.set(nftData.mintAddress, nftData);

    return {
      success: true,
      data: nftData,
      error: null,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch NFT';
    return {
      success: false,
      data: null,
      error: message,
    };
  }
}
